import { useCallback, useMemo } from 'react';

import { AlertTriangle, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { useIntegrationStatus } from '@/modules/onboarding/integrations-setup/hooks/use-integration-status';
import { AuthenticatedRoutes } from '@/router/routes';

import { OrganizationIntegrationStatus } from '../../../types/integration-status';
import { ConflictsState } from './conflicts-state';
import { ResolvingConflictsState } from './resolving-conflicts-state';
import { SyncingState } from './syncing-state';

interface IntegrationStatusCardProps {
  isCollapsed?: boolean;
}

enum IntegrationStatusCardState {
  Resolving = 'resolving',
  Syncing = 'syncing',
  Conflicts = 'conflicts',
  Hidden = 'hidden',
}

/**
 * Integration Status Card
 * Displays the current status of integrations in the sidebar
 *
 * States:
 * 1. Syncing - When integrations are being synchronized
 * 2. Conflicts - When data conflicts are detected (requires user action)
 * 3. Resolving - When user is on data reconciliation pages
 * 4. Hidden - Any other state (idle, error, unknown)
 *
 * Collapsed mode:
 * - Syncing: Shows spinning loader icon only
 * - Conflicts: Shows warning icon only
 * - Resolving: Hidden
 */
export function IntegrationStatusCard({ isCollapsed = false }: IntegrationStatusCardProps) {
  const { t } = useTranslation('authenticated.settings');
  const location = useLocation();
  const navigate = useNavigate();
  const { data: integrationStatusData } = useIntegrationStatus();

  // Check if user is currently on a data reconciliation page
  const isResolvingConflicts = useMemo(() => {
    return location.pathname.startsWith(AuthenticatedRoutes.DataReconciliation);
  }, [location.pathname]);

  // Determine current state
  const currentState = useMemo(() => {
    if (!integrationStatusData) return IntegrationStatusCardState.Hidden;

    // If user is on reconciliation page, show resolving state
    if (isResolvingConflicts) {
      return IntegrationStatusCardState.Resolving;
    }

    // Show syncing state
    if (integrationStatusData.status === OrganizationIntegrationStatus.Syncing) {
      return IntegrationStatusCardState.Syncing;
    }

    // Show conflicts state
    if (integrationStatusData.status === OrganizationIntegrationStatus.HasConflicts) {
      return IntegrationStatusCardState.Conflicts;
    }

    // Hide card for other states (Idle, SyncFailed, Unknown)
    return IntegrationStatusCardState.Hidden;
  }, [integrationStatusData, isResolvingConflicts]);

  // Handle resolve button click
  const handleResolve = useCallback(() => {
    // Navigate to the first data reconciliation route
    navigate(AuthenticatedRoutes.DataReconciliation);
  }, [navigate]);

  // Don't render card if no state to show
  if (currentState == IntegrationStatusCardState.Hidden) {
    return null;
  }

  // Hide resolving state in collapsed mode
  if (isCollapsed && currentState === IntegrationStatusCardState.Resolving) {
    return null;
  }

  // Render collapsed view with icons only
  if (isCollapsed) {
    switch (currentState) {
      case IntegrationStatusCardState.Syncing:
        return (
          <SidebarMenuButton tooltip={t('pages.integrations.status.syncing.tooltip')}>
            <Loader className="h-4 w-4 animate-spin" />
          </SidebarMenuButton>
        );
      case IntegrationStatusCardState.Conflicts:
        return (
          <SidebarMenuButton
            tooltip={t('pages.integrations.status.conflicts.tooltip')}
            onClick={handleResolve}
          >
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </SidebarMenuButton>
        );
      default:
        return null;
    }
  }

  const renderContent = () => {
    // Render full card view for expanded sidebar
    switch (currentState) {
      case IntegrationStatusCardState.Syncing:
        return <SyncingState />;
      case IntegrationStatusCardState.Conflicts:
        return <ConflictsState onResolve={handleResolve} />;
      case IntegrationStatusCardState.Resolving:
        return <ResolvingConflictsState />;
      default:
        return null;
    }
  };

  return (
    <div className="px-1">
      <Card className="w-full overflow-hidden bg-card">
        <CardContent className="flex gap-3 p-4">{renderContent()}</CardContent>
      </Card>
    </div>
  );
}
